import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "assets/logo.png";
import imageTest from "assets/imageTest.png";
import { Button, Form, Alert } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  clientLogin,
  createAuthenticatedAxiosInstance,
  loginWithGoogle,
} from "../../api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomNavbarClients from "./components/navBarClients/CustomNavbarClients";
import { GoogleJwtPayload } from "../../interfaces/IGoogle";
import { useUserStore } from "../../zustand/userStore";
import "./LoginClients.scss";
import { useBreakpoints } from "hooks/useBreakPoints";

/**
 * Login component with Google login
 */
const LoginClients: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { setToken, setAuthProvider } = useAuthStore();
  const { setFavoriteProducts } = useUserStore();
  const { isMobile } = useBreakpoints();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Limpiar cualquier estado problemático al cargar la página de login
    localStorage.removeItem("returnToOrder");

    // Check if there's a returnUrl in location state
    if (location.state?.returnUrl) {
      localStorage.setItem("loginReturnUrl", location.state.returnUrl);
    }
  }, [location.state]);

  /**
   * Effect for error timeout
   */
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(false);
        setErrorMessage("");
      }, 8000); // Aumentado a 8 segundos para errores más largos
      return () => clearTimeout(timer);
    }
  }, [error]);

  /**
   * Handle login action
   */
  const handleLogin = async () => {
    try {
      const response = await clientLogin(username, password);

      if (response.success) {
        setToken(response.token);
        // Establecer el proveedor como "email" para login tradicional
        setAuthProvider("email");

        // Check if there's a return URL
        const returnUrl = localStorage.getItem("loginReturnUrl");
        localStorage.removeItem("loginReturnUrl");

        if (returnUrl) {
          navigate(returnUrl);
        } else {
          navigate("/");
        }
      } else {
        setError(true);
        setErrorMessage("Correo electrónico o contraseña incorrectos");
        setUsername("");
        setPassword("");
      }
    } catch (error) {
      setError(true);
      setErrorMessage(
        "Error al iniciar sesión. Por favor, intenta nuevamente."
      );
      setUsername("");
      setPassword("");
    }
  };

  /**
   * Handle forgot password action
   */
  const handleForgotPassword = () => {
    // Logic for password recovery here
  };

  /**
   * Handle Google login success
   */
  const handleGoogleLoginSuccess = async (
    credentialResponse: CredentialResponse
  ) => {
    setIsGoogleLoading(true);
    setError(false);
    setErrorMessage("");

    try {
      // Validar que tenemos el credential
      if (!credentialResponse.credential) {
        throw new Error("No se recibió el token de Google");
      }

      // Decodificar el token de Google
      const credentialResponseDecoded: GoogleJwtPayload = jwtDecode(
        credentialResponse.credential
      );

      const email = credentialResponseDecoded.email;
      const name = credentialResponseDecoded.name;

      // Validar que tenemos los datos necesarios
      if (!email || !name) {
        throw new Error("Datos incompletos recibidos de Google");
      }

      const response = await loginWithGoogle(
        email,
        name,
        credentialResponse.credential
      );

      if (response.success) {
        setToken(response.token);
        const axiosInstance = createAuthenticatedAxiosInstance(response.token);
        setAuthProvider("google");

        // Check if there's a return URL
        const returnUrl = localStorage.getItem("loginReturnUrl");
        localStorage.removeItem("loginReturnUrl");

        if (returnUrl) {
          navigate(returnUrl);
        } else {
          navigate("/");
        }
      } else {
        console.error("Login failed:", response);
        setError(true);
        setErrorMessage(
          response.message || "Error al iniciar sesión con Google"
        );
      }
    } catch (error: any) {
      console.error("Error completo al iniciar sesión con Google:", error);

      setError(true);

      // Manejo específico de errores
      if (error.message) {
        if (error.message.includes("500")) {
          setErrorMessage(
            "Error del servidor. Por favor, intenta más tarde o contacta al soporte técnico."
          );
        } else if (error.message.includes("400")) {
          setErrorMessage(
            "Error de validación. Por favor, intenta nuevamente."
          );
        } else if (error.message.includes("401")) {
          setErrorMessage(
            "Credenciales de Google inválidas. Por favor, intenta nuevamente."
          );
        } else if (error.message.includes("Network Error")) {
          setErrorMessage(
            "Error de conexión. Verifica tu conexión a internet."
          );
        } else {
          setErrorMessage(error.message);
        }
      } else {
        setErrorMessage(
          "Error inesperado al iniciar sesión con Google. Por favor, intenta nuevamente."
        );
      }

      // Log adicional para debugging
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
        console.error("Error response headers:", error.response.headers);
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  /**
   * Handle Google login error
   */
  const handleGoogleLoginError = () => {
    setError(true);
    setErrorMessage(
      "Error al conectar con Google. Por favor, intenta nuevamente."
    );
  };

  /**
   * Handle focus action
   */
  const handleFocus = () => {
    setError(false);
    setErrorMessage("");
  };

  const handleLogout = () => {
    // Logic for logout here
    localStorage.clear();
    setToken(null);
    navigate("/");
    setFavoriteProducts([]);
  };

  return (
    <>
      <CustomNavbarClients />
      <div className="container-fluid d-flex p-0 login-container">
        <Button
          variant="secondary"
          onClick={handleLogout}
          className="back-button"
        >
          <FontAwesomeIcon
            icon={faArrowLeft}
            style={{ paddingRight: isMobile ? "0" : "0.5rem" }}
          />
          <span className="logout-text" style={{ marginLeft: "5px" }}>
            Salir
          </span>
        </Button>
        <div className="col-6 d-flex flex-column align-items-center justify-content-start p-1 login-form">
          <img
            src={logo}
            alt="Logo de la Boutique"
            className="mb-4"
            style={{ width: "215px" }}
          />
          <div className="w-100" style={{ maxWidth: "400px" }}>
            <Form>
              <h1 className="mb-4 text-center fw-bold">Iniciar Sesión</h1>

              {/* Alert para errores más detallados */}
              {error && errorMessage && (
                <Alert variant="danger" className="text-center mb-3">
                  {errorMessage}
                </Alert>
              )}

              <Form.Group className="mb-3" controlId="formBasicEmail">
                <Form.Control
                  type="text"
                  placeholder="Correo electrónico"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={handleFocus}
                  className={`mb-2 mx-auto ${
                    error && !errorMessage.includes("Google")
                      ? "is-invalid"
                      : ""
                  }`}
                  style={{ width: "55%" }}
                />
              </Form.Group>

              <Form.Group className="mb-3" controlId="formBasicPassword">
                <Form.Control
                  type="password"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handleFocus}
                  className={`mx-auto ${
                    error && !errorMessage.includes("Google")
                      ? "is-invalid"
                      : ""
                  }`}
                  style={{ width: "55%" }}
                />
                {/* Solo mostrar el error de credenciales para login tradicional */}
                {error && !errorMessage.includes("Google") && !errorMessage && (
                  <div className="text-danger mt-2 text-center">
                    Correo electrónico o contraseña incorrectos
                  </div>
                )}
              </Form.Group>

              <div className="d-flex justify-content-center">
                <Button
                  variant="primary"
                  type="button"
                  onClick={handleLogin}
                  className="fw-bold align-item-center text-center"
                  style={{ width: "55%" }}
                  disabled={isGoogleLoading}
                >
                  Continuar
                </Button>
              </div>

              <div className="d-flex justify-content-center mt-3">
                {isGoogleLoading ? (
                  <div className="text-center">
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    Iniciando sesión con Google...
                  </div>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleLoginSuccess}
                    onError={handleGoogleLoginError}
                  />
                )}
              </div>

              <Button
                variant="link"
                onClick={handleForgotPassword}
                className="w-100 text-center mt-2"
              >
                Olvidé mi contraseña
              </Button>
            </Form>

            <hr
              style={{
                border: "1px solid black",
                width: "55%",
                textAlign: "center",
                margin: "auto",
              }}
            />

            <div className="mt-1 text-center fw-bold">
              <span>¿No tienes una cuenta?</span>
            </div>
            <div className="d-flex justify-content-center">
              <Button
                variant="primary"
                onClick={() => navigate("/register")}
                className="mt-1 fw-bold align-item-center text-center"
                style={{ width: "55%" }}
                disabled={isGoogleLoading}
              >
                Registrarse
              </Button>
            </div>
          </div>
        </div>
        <div
          className="login-image"
          style={{
            height: "auto",
            width: "50%",
            display: isMobile ? "none" : "cover",
          }}
        >
          <img
            src={imageTest}
            alt="Imagen de la Boutique"
            className="img-fluid w-100 h-100"
            style={{ objectFit: "cover" }}
          />
        </div>
      </div>
    </>
  );
};

export default LoginClients;
